'use client'

import { useEffect, useRef, useState } from 'react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  ChallengeRequiredRefusal,
  DownloadCooldownRefusal,
  UpsellRequiredRefusal,
  parseInstant,
} from '@/lib/download-protection/refusals'
import { sendDownloadProtectionEvent } from '@/lib/analytics/download-protection'
import {
  UPSELL_LABELS,
  UpsellOutcome,
  recordUpsellOutcome,
} from '@/lib/download-protection/upsell'
import {
  TurnstileCancelled,
  TurnstileWidget,
  renderTurnstile,
} from '@/lib/download-protection/turnstile'
import { useDownloadRefusalStore } from '@/lib/store/download-refusal-store'

/**
 * The frame all three refusal dialogs are built in. Written once so a change to the width,
 * background, or padding cannot reach one dialog and miss the other two.
 */
const REFUSAL_DIALOG_CLASS = '!tw-max-w-lg !tw-bg-[var(--Primary)] !tw-p-6'
const REFUSAL_DIALOG_HEADER_CLASS = 'tw-space-y-2 tw-text-left'

/**
 * Renders the dialogs a refused download needs. Mounted once, beside the toaster, because
 * the download hook meets a refusal inside a callback and cannot render a dialog from there.
 *
 * A refusal code that needs a dialog gets a variant in `@/lib/download-protection/refusals`
 * and a body here; no consumer of `useAsset` has to wire anything up.
 */
export function DownloadRefusalDialogs() {
  const refusal = useDownloadRefusalStore((state) => state.refusal)
  const retry = useDownloadRefusalStore((state) => state.retry)
  const sequence = useDownloadRefusalStore((state) => state.sequence)
  const dismiss = useDownloadRefusalStore((state) => state.dismiss)

  if (refusal?.code === 'DOWNLOAD_COOLDOWN') {
    return <CooldownDialog refusal={refusal} onClose={dismiss} />
  }

  if (refusal?.code === 'CHALLENGE_REQUIRED') {
    return (
      <ChallengeDialog
        // A refused token arrives as a second refusal, which bumps the sequence. Remounting
        // on it is the reset: the spent widget goes away with its iframe and a fresh
        // challenge takes its place, without the dialog ever closing.
        key={sequence}
        refusal={refusal}
        canRetry={Boolean(retry)}
        onToken={async (token) => {
          await retry?.(token)

          // The retry either started the download or was refused again. A second refusal has
          // already replaced what this dialog is showing, so only close when nothing did.
          const store = useDownloadRefusalStore.getState()

          if (store.sequence === sequence) {
            store.dismiss()
          }
        }}
        onClose={dismiss}
      />
    )
  }

  if (refusal?.code === 'UPSELL_REQUIRED') {
    return (
      <UpsellDialog
        // A refusal arriving while this one is open replaces it, the same as a challenge.
        key={sequence}
        refusal={refusal}
        canRetry={Boolean(retry)}
        onAnswered={() => {
          dismiss()
          void retry?.()
        }}
        onClose={dismiss}
      />
    )
  }

  return null
}

/**
 * Tells the member their downloads are paused, when they resume, and where to ask about it.
 * Closing it does nothing else: the download is not retried.
 */
function CooldownDialog({ refusal, onClose }: { refusal: DownloadCooldownRefusal; onClose: () => void }) {
  const resumesAt = formatResumeTime(refusal.ends_at)

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) {
          onClose()
        }
      }}
    >
      <DialogContent className={REFUSAL_DIALOG_CLASS}>
        <DialogHeader className={REFUSAL_DIALOG_HEADER_CLASS}>
          <DialogTitle>Downloads are paused</DialogTitle>
          <DialogDescription>{refusal.message ?? 'Your downloads are paused for now.'}</DialogDescription>
        </DialogHeader>
        {resumesAt ? <p className="tw-text-sm">Downloads resume on {resumesAt}.</p> : null}
        <DialogFooter className="tw-gap-2">
          {refusal.support_url ? (
            <Button asChild variant="stroke">
              <a href={refusal.support_url} target="_blank" rel="noopener noreferrer">
                Contact support
              </a>
            </Button>
          ) : null}
          <Button onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/** The resume moment in the member's own locale, or null when the API did not send one. */
function formatResumeTime(endsAt?: string): string | null {
  const instant = parseInstant(endsAt)

  if (instant === null) {
    return null
  }

  return new Date(instant).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
}

/**
 * Asks the member to pass a Turnstile challenge, then hands the token back so the refused
 * download can be sent again carrying it.
 *
 * Most members never touch this: the widget settles by itself and `onToken` fires within a
 * second or two. The dialog stays up while the retried download is in flight, because a
 * token the API refuses brings the member straight back here, and closing and reopening
 * would look like a fault rather than a second try.
 *
 * Closing cancels the challenge: nothing is sent, and the download stays failed. Once the
 * token has gone out the request cannot be recalled, so the dialog holds itself open for
 * the moment it takes to answer rather than offering a cancel it could not honour.
 */
function ChallengeDialog({
  refusal,
  canRetry,
  onToken,
  onClose,
}: {
  refusal: ChallengeRequiredRefusal
  canRetry: boolean
  onToken: (token: string) => Promise<void>
  onClose: () => void
}) {
  // A callback ref, not `useRef`: Radix renders its portal empty on the first render, so a
  // ref object is still null when this component's effect runs and the widget would never be
  // asked for. State makes the node itself a dependency, so rendering waits for it to attach.
  const [container, setContainer] = useState<HTMLDivElement | null>(null)
  const [status, setStatus] = useState<ChallengeStatus>('rendering')

  // `onToken` is a fresh closure on every parent render, so the effect below must not
  // depend on it: doing so would tear the widget down and render a second one.
  const onTokenRef = useRef(onToken)
  onTokenRef.current = onToken

  const siteKey = refusal.site_key

  useEffect(() => {
    if (!siteKey) {
      // crate-core has the challenge switched on without a site key configured. Nothing the
      // member does will get them past this, so log it: they will be told to contact support
      // and support needs something to go on.
      console.error('A download challenge arrived without a Turnstile site key; no widget can be shown.')
      sendDownloadProtectionEvent('download_protection_challenge_failed', {
        refusal_code: 'CHALLENGE_REQUIRED',
        reason: 'unconfigured',
      })
      setStatus('unconfigured')
      return
    }

    if (!container) {
      // The portal has not attached the node yet. This effect runs again when it does.
      return
    }

    let widget: TurnstileWidget | null = null
    let isCancelled = false

    void renderTurnstile(container, siteKey, {
      isCancelled: () => isCancelled,
      onToken: (token) => {
        if (isCancelled) {
          return
        }

        if (!canRetry) {
          // Nothing to send the token on: this download path handed over no retry. Say so
          // rather than leaving the member watching a widget that has already succeeded.
          setStatus('passed')
          sendDownloadProtectionEvent('download_protection_challenge_completed', {
            refusal_code: 'CHALLENGE_REQUIRED',
            can_retry: false,
          })
          return
        }

        setStatus('verifying')
        sendDownloadProtectionEvent('download_protection_challenge_completed', {
          refusal_code: 'CHALLENGE_REQUIRED',
          can_retry: true,
        })

        onTokenRef.current(token).catch((error) => {
          // Every download path handles its own errors, so this only fires if one ever stops
          // doing so. Say the check failed rather than leaving "Starting your download..." up.
          console.error('The challenged download could not be retried:', error)

          if (!isCancelled) {
            sendDownloadProtectionEvent('download_protection_challenge_failed', {
              refusal_code: 'CHALLENGE_REQUIRED',
              reason: 'retry_error',
            })
            setStatus('failed')
          }
        })
      },
      onFailure: (failure) => {
        if (!isCancelled) {
          sendDownloadProtectionEvent('download_protection_challenge_failed', {
            refusal_code: 'CHALLENGE_REQUIRED',
            reason: failure,
          })
          setStatus(failure === 'expired' ? 'expired' : 'failed')
        }
      },
    })
      .then((rendered) => {
        widget = rendered

        if (isCancelled) {
          // The dialog closed while the script was still loading.
          rendered.remove()
          return
        }

        setStatus('waiting')
      })
      .catch((error) => {
        if (error instanceof TurnstileCancelled) {
          return
        }

        console.error('Turnstile could not be shown:', error)

        if (!isCancelled) {
          sendDownloadProtectionEvent('download_protection_challenge_failed', {
            refusal_code: 'CHALLENGE_REQUIRED',
            reason: 'render_error',
          })
          setStatus('failed')
        }
      })

    return () => {
      isCancelled = true
      widget?.remove()
    }
  }, [canRetry, container, siteKey])

  // The retried download is already on its way and cannot be taken back, so the dialog
  // refuses to close for the moment it takes to answer. It closes itself either way: on a
  // success the caller dismisses it, on a refusal a fresh challenge replaces it.
  const isLocked = status === 'verifying'

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open && !isLocked) {
          onClose()
        }
      }}
    >
      <DialogContent className={REFUSAL_DIALOG_CLASS}>
        <DialogHeader className={REFUSAL_DIALOG_HEADER_CLASS}>
          <DialogTitle>Confirm you are not a robot</DialogTitle>
          <DialogDescription>
            {refusal.message ?? 'Please confirm you are not a robot to continue downloading.'}
          </DialogDescription>
        </DialogHeader>
        <div ref={setContainer} className="tw-flex tw-min-h-[65px] tw-justify-center" />
        <p className="tw-text-sm" role="status" aria-live="polite">
          {CHALLENGE_STATUS_MESSAGES[status]}
        </p>
        <DialogFooter className="tw-gap-2">
          <Button variant="stroke" onClick={onClose} disabled={isLocked}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/**
 * Offers the member an Enterprise plan, and reports back what they did with the offer.
 *
 * Either button is an answer, and an answer is the only thing that lifts the refusal, so
 * both record an outcome and then let the refused download run again. The download is
 * retried rather than assumed to succeed: crate-core decides whether the answer lifted the
 * refusal, and if it did not, the modal comes back instead of the download silently never
 * starting.
 *
 * The call to action is a real link, so the browser opens the tab itself. Opening it from
 * JavaScript after waiting on the outcome call would have lost the member's gesture and
 * been swallowed by the popup blocker.
 *
 * Closing without answering is not an answer: nothing is recorded and nothing is retried,
 * so the next download is refused again and the offer returns.
 */
function UpsellDialog({
  refusal,
  canRetry,
  onAnswered,
  onClose,
}: {
  refusal: UpsellRequiredRefusal
  canRetry: boolean
  onAnswered: () => void
  onClose: () => void
}) {
  const [isRecording, setIsRecording] = useState(false)

  const penaltyId = refusal.penalty_id

  const answer = async (outcome: UpsellOutcome) => {
    if (isRecording) {
      return
    }

    sendDownloadProtectionEvent('download_protection_upsell_answered', {
      refusal_code: 'UPSELL_REQUIRED',
      outcome,
    })

    if (penaltyId === undefined) {
      // Nothing to record the answer against, so nothing would lift the refusal either.
      // Retrying would only earn a second refusal, so close and leave the download failed.
      console.error('An upsell refusal arrived without a penalty id; its outcome cannot be recorded.')
      onClose()
      return
    }

    setIsRecording(true)

    try {
      await recordUpsellOutcome(penaltyId, outcome)
    } catch (error) {
      // The retry below is refused again when the outcome never landed, which brings this
      // dialog back. That is the member-visible answer; this is for us.
      console.error('The upsell outcome could not be recorded:', error)
    }

    onAnswered()
  }

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open && !isRecording) {
          onClose()
        }
      }}
    >
      <DialogContent className={REFUSAL_DIALOG_CLASS}>
        <DialogHeader className={REFUSAL_DIALOG_HEADER_CLASS}>
          <DialogTitle>Upgrade to Enterprise</DialogTitle>
          <DialogDescription>
            {refusal.message ?? 'This account needs an Enterprise plan to keep downloading.'}
          </DialogDescription>
        </DialogHeader>
        {canRetry ? null : (
          // This download path handed over no retry, so answering cannot restart it. Say so
          // rather than closing the dialog onto nothing.
          <p className="tw-text-sm">Start the download again once you have chosen.</p>
        )}
        <DialogFooter className="tw-gap-2">
          <Button variant="stroke" onClick={() => void answer('dismissed')} disabled={isRecording}>
            {UPSELL_LABELS.dismissed}
          </Button>
          {refusal.cta_url ? (
            // No `disabled` here: it does nothing on an anchor. `answer` guards itself, so a
            // second press records nothing twice — it only opens a second tab.
            <Button asChild>
              <a
                href={refusal.cta_url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => void answer('clicked')}
              >
                {UPSELL_LABELS.clicked}
              </a>
            </Button>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

type ChallengeStatus = 'rendering' | 'waiting' | 'verifying' | 'passed' | 'expired' | 'failed' | 'unconfigured'

const CHALLENGE_STATUS_MESSAGES: Record<ChallengeStatus, string> = {
  rendering: 'Loading the check...',
  waiting: 'This usually finishes by itself.',
  verifying: 'Starting your download...',
  passed: 'Check passed. Start the download again.',
  expired: 'The check expired. Close this and try the download again.',
  failed: 'The check could not be loaded. Close this and try the download again.',
  unconfigured: 'This check is not available right now. Please contact support.',
}
